import { useState } from 'react';
import { Button } from '@/ui/Button';
import { Card } from '@/ui/Card';
import { ConfirmDialog } from '@/ui/Dialog';
import { Drawer } from '@/ui/Drawer';
import { Field } from '@/ui/Field';
import { Skeleton } from '@/ui/Skeleton';
import { EstadoError } from '@/components/EstadoError';
import { EstadoVacio } from '@/components/EstadoVacio';
import {
  MAXIMO_DE_FOTOS, TOPE_MB_POR_FOTO, useBorrarFoto, useEvento, useLimpiarDuplicados,
  usePublicarGaleria, useSubirFotos, type FotoDeEvento,
} from './api';

/**
 * Galeria.tsx — la pantalla de `#event-gallery/<id>`. Módulo 8, opción 7.
 *
 * ## Las fotos existen para el teclado
 *
 * El vanilla ponía el `onclick` en el `<img>`: medido el 2026-09-17, **0 de 6
 * fotos alcanzables con Tab**, y las seis con el mismo `alt="Foto"`. Aquí cada
 * foto es un botón que abre el visor del portal, con un nombre que dice cuál es.
 *
 * ## La subida no finge un progreso
 *
 * El vanilla escribía «Subiendo 0/6…» y el número no se movía nunca:
 * `Uploader.uploadBatch` manda todo en una petición y no informa de nada. Si
 * fallaba, el botón se quedaba con ese texto. Ahora dice «Subiendo 6 fotos…»
 * mientras dura, y nada más.
 *
 * ## Los pies de foto no se pintan
 *
 * `EventPhoto.caption` existe, pero ningún endpoint lo escribe —0 de 6 en la
 * base—. Y el vanilla lo metía crudo en el HTML dos veces. Código sin datos que
 * además era un agujero: **Muerto**.
 *
 * ## La gestión pregunta el permiso
 *
 * Tres `role !== 'colaborador'` en el vanilla; el servidor exige
 * `eventos:manage` para subir, borrar, limpiar y publicar.
 */
export function Galeria() {
  const puedeGestionar = Auth.can('eventos', 'manage');
  const [id] = useState(() => {
    const p = window._routeParams?.[0];
    return p && /^\d+$/.test(p) ? Number(p) : null;
  });
  const consulta = useEvento(id);
  const e = consulta.data;

  const [subiendo, setSubiendo] = useState(false);
  const [confirmando, setConfirmando] = useState<'limpiar' | 'publicar' | null>(null);
  const [borrando, setBorrando] = useState<{ foto: FotoDeEvento; numero: number } | null>(null);

  const limpiar = useLimpiarDuplicados(id ?? 0);
  const publicar = usePublicarGaleria(id ?? 0);
  const borrar = useBorrarFoto(id ?? 0);

  if (id == null) {
    return (
      <EstadoVacio
        icono="bi-images"
        titulo="No se indicó ningún evento"
        descripcion="La galería se abre desde el detalle de un evento."
        accion={{ texto: 'Ir a Eventos', onClick: () => APP.navigate('events') }}
      />
    );
  }

  const fotos = e?.gallery ?? [];
  const aviso = (err: unknown, porDefecto: string) =>
    Utils.toast(err instanceof Error ? err.message : porDefecto, 'error');

  function confirmar() {
    if (confirmando === 'limpiar') {
      limpiar.mutate(undefined, {
        onSuccess: (r) => { setConfirmando(null); Utils.toast(r.message, 'success'); },
        onError: (err) => { setConfirmando(null); aviso(err, 'No se pudieron limpiar los duplicados'); },
      });
    } else if (confirmando === 'publicar') {
      publicar.mutate(undefined, {
        onSuccess: (r) => {
          setConfirmando(null);
          Utils.toast(r.ya_existia ? 'La galería ya estaba publicada en el muro' : 'Galería publicada en el muro',
            r.ya_existia ? 'info' : 'success');
        },
        onError: (err) => { setConfirmando(null); aviso(err, 'No se pudo publicar la galería'); },
      });
    }
  }

  function confirmarBorrado() {
    if (!borrando) return;
    borrar.mutate(borrando.foto.id, {
      onSuccess: () => { setBorrando(null); Utils.toast('Foto eliminada', 'success'); },
      onError: (err) => { setBorrando(null); aviso(err, 'No se pudo eliminar la foto'); },
    });
  }

  return (
    <>
      <a
        href={`#eventos/${id}`}
        className="mb-3 inline-flex min-h-9 items-center gap-1.5 rounded-xs text-2 font-semibold text-brand-ink
                   hover:underline focus-visible:focus-anillo pointer-coarse:min-h-11"
      >
        <i className="bi bi-arrow-left" aria-hidden="true" /> Volver al evento
      </a>

      {consulta.isLoading ? (
        <div className="flex flex-col gap-4">
          <Skeleton className="h-8 w-1/2" />
          <Skeleton className="h-64 w-full" />
        </div>
      ) : consulta.isError ? (
        <Card className="p-4">
          <EstadoError err={consulta.error} que="la galería del evento" onReintentar={() => void consulta.refetch()} />
        </Card>
      ) : e ? (
        <>
          <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <h1 className="flex items-center gap-2 text-6 font-bold text-brand-ink [overflow-wrap:anywhere]">
                <i className="bi bi-images" aria-hidden="true" />
                {e.title}
              </h1>
              <p className="mt-1 text-3 text-muted">
                {Utils.fmtDatetime(e.event_date)} · {fotos.length} {fotos.length === 1 ? 'foto' : 'fotos'}
              </p>
            </div>
            {puedeGestionar && (
              <div className="flex flex-wrap gap-2">
                {fotos.length > 0 && (
                  <>
                    <Button variant="ghost" onClick={() => setConfirmando('limpiar')}>
                      <i className="bi bi-stars" aria-hidden="true" /> Limpiar duplicados
                    </Button>
                    <Button variant="secondary" onClick={() => setConfirmando('publicar')}>
                      <i className="bi bi-megaphone" aria-hidden="true" /> Publicar galería
                    </Button>
                  </>
                )}
                <Button onClick={() => setSubiendo(true)}>
                  <i className="bi bi-camera" aria-hidden="true" /> Subir fotos
                </Button>
              </div>
            )}
          </header>

          {!fotos.length ? (
            <EstadoVacio
              icono="bi-camera"
              titulo="Todavía no hay fotos"
              descripcion={puedeGestionar
                ? 'Sube las fotos del evento para armar la galería.'
                : 'Cuando Bienestar suba las fotos del evento, aparecerán aquí.'}
              accion={puedeGestionar ? { texto: 'Subir fotos', onClick: () => setSubiendo(true) } : undefined}
            />
          ) : (
            <ul className="grid gap-3 [grid-template-columns:repeat(auto-fill,minmax(180px,1fr))]">
              {fotos.map((f, i) => (
                <li key={f.id} className="relative">
                  <button
                    type="button"
                    onClick={() => Utils.openLightbox(fotos.map((x) => ({ url: x.url, thumb_url: x.thumb_url })), i)}
                    aria-label={`Ver la foto ${i + 1} de ${fotos.length}`}
                    className="block aspect-[4/3] w-full overflow-hidden rounded-sm border border-line bg-sunken
                               focus-visible:focus-anillo"
                  >
                    <img src={f.thumb_url || f.url} alt="" loading="lazy" className="size-full object-cover" />
                  </button>
                  {puedeGestionar && (
                    <Button
                      variant="secondary"
                      size="sm"
                      iconOnly
                      aria-label={`Eliminar la foto ${i + 1}`}
                      onClick={() => setBorrando({ foto: f, numero: i + 1 })}
                      className="absolute right-1.5 top-1.5 bg-surface"
                    >
                      <i className="bi bi-trash3" aria-hidden="true" />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </>
      ) : null}

      {puedeGestionar && (
        <SubirFotos idEvento={id} abierto={subiendo} onCerrar={() => setSubiendo(false)} />
      )}

      <ConfirmDialog
        abierto={confirmando === 'limpiar'}
        onCambio={(v) => { if (!v) setConfirmando(null); }}
        titulo="Limpiar duplicados"
        descripcion="Elimina los registros que apuntan a la misma imagen, que quedaron de subidas antiguas. Las fotos distintas no se tocan."
        textoConfirmar="Limpiar"
        cargando={limpiar.isPending}
        onConfirmar={confirmar}
      />
      <ConfirmDialog
        abierto={confirmando === 'publicar'}
        onCambio={(v) => { if (!v) setConfirmando(null); }}
        titulo="Publicar la galería en el muro"
        descripcion="La galería aparecerá en Novedades para todo el portal. Si ya estaba publicada, no se repite."
        textoConfirmar="Publicar"
        cargando={publicar.isPending}
        onConfirmar={confirmar}
      />
      <ConfirmDialog
        abierto={borrando != null}
        onCambio={(v) => { if (!v) setBorrando(null); }}
        titulo="Eliminar la foto"
        descripcion={borrando ? `La foto ${borrando.numero} se borra de la galería y del almacenamiento. No se puede deshacer.` : ''}
        textoConfirmar="Eliminar"
        destructivo
        cargando={borrar.isPending}
        onConfirmar={confirmarBorrado}
      />
    </>
  );
}

function SubirFotos({ idEvento, abierto, onCerrar }: { idEvento: number; abierto: boolean; onCerrar: () => void }) {
  const [fotos, setFotos] = useState<File[]>([]);
  const [error, setError] = useState('');
  const subir = useSubirFotos(idEvento);

  function cerrar() {
    setFotos([]);
    setError('');
    onCerrar();
  }

  function elegir(lista: FileList | null) {
    const elegidas = Array.from(lista ?? []);
    const grandes = elegidas.filter((f) => f.size > TOPE_MB_POR_FOTO * 1024 * 1024);
    if (elegidas.length > MAXIMO_DE_FOTOS) {
      setError(`Elegiste ${elegidas.length} fotos; el máximo por envío es ${MAXIMO_DE_FOTOS}.`);
      setFotos([]);
    } else if (grandes.length) {
      setError(`${grandes.length === 1 ? 'Una foto pasa' : `${grandes.length} fotos pasan`} de ${TOPE_MB_POR_FOTO} MB: ${grandes.map((f) => f.name).join(', ')}.`);
      setFotos([]);
    } else {
      setError('');
      setFotos(elegidas);
    }
  }

  function enviar() {
    if (!fotos.length) { setError('Elige al menos una foto.'); return; }
    subir.mutate(fotos, {
      onSuccess: (r) => {
        Utils.toast(`${r.uploaded} ${r.uploaded === 1 ? 'foto subida' : 'fotos subidas'}`, 'success');
        cerrar();
      },
      onError: (err) => setError(err instanceof Error ? err.message : 'No se pudieron subir las fotos.'),
    });
  }

  return (
    <Drawer
      abierto={abierto}
      onCambio={(v) => { if (!v) cerrar(); }}
      titulo="Subir fotos"
      descripcion={`Hasta ${MAXIMO_DE_FOTOS} por envío, de ${TOPE_MB_POR_FOTO} MB cada una. Se comprimen al subirlas.`}
      ancho="sm"
      pie={
        <>
          <Button variant="ghost" onClick={cerrar}>Cancelar</Button>
          <Button onClick={enviar} loading={subir.isPending} disabled={!fotos.length}>
            <i className="bi bi-cloud-upload" aria-hidden="true" />
            {subir.isPending
              ? `Subiendo ${fotos.length} ${fotos.length === 1 ? 'foto' : 'fotos'}…`
              : fotos.length ? `Subir ${fotos.length} ${fotos.length === 1 ? 'foto' : 'fotos'}` : 'Subir'}
          </Button>
        </>
      }
    >
      <Field label="Fotos" id="galeria-fotos" error={error} ayuda="JPG o PNG.">
        {/* `id` y `aria-describedby` a mano: un `<input>` crudo no los toma del
            contexto de `Field`. */}
        <input
          id="galeria-fotos"
          aria-describedby="galeria-fotos-ayuda"
          aria-invalid={Boolean(error)}
          type="file"
          accept="image/*"
          multiple
          onChange={(ev) => elegir(ev.target.files)}
          className="block w-full text-2 file:mr-3 file:min-h-9 pointer-coarse:file:min-h-11
                     file:rounded-sm file:border-0 file:bg-sunken file:px-3 file:text-2
                     file:font-semibold file:text-ink"
        />
      </Field>
      {fotos.length > 0 && (
        <p className="mt-3 text-2 text-ink-2" aria-live="polite">
          {fotos.length} {fotos.length === 1 ? 'foto elegida' : 'fotos elegidas'}
        </p>
      )}
    </Drawer>
  );
}
